import { PapiDeviation } from '@wix/da-papi-types';
import { Url } from '@wix/da-url';
import { getDeviationType, getPrimaryTier, isDeviationAuthor } from '../../DeviationViews/_helpers';
import { DeviationTypes } from '../../types/deviation';
import { usePrimaryTier } from '../../Tier/PrimaryTierContext';
import { useCurrentUser } from './useCurrentUser';

export interface UseSubscriptionTierReturnType {
  /** is the given deviation a tier? */
  isTier: boolean;
  /** does the given deviation belong to a tier? */
  isInTier: boolean;
  /** the tier the given deviation is part of (if any) or the deviation itself if it is a tier */
  tier?: PapiDeviation;
  /** is the current user the owner of the tier and deviation? */
  isOwner: boolean;
  /** is the current user subscribed to the associated tier? */
  isUserSubscribed: boolean;
  /** ponies url for purchasing tier subscription */
  tierCheckoutUrl?: string;
  /** UP url for all of author's subscriptions */
  subscriptionsTabUrl?: string;
  /** should the current user be blocked from seeing this content because they're unsubscribed. */
  needsSubscription: boolean;
  /** is the user subscribed but still blocked from viewing content (i.e. due to subscription
   *  settings, etc.)? the same as `tierAccess === 'locked-subscribed'` under the hood */
  isSubscribedButBlocked: boolean;
}

/** returns member subscription info for the given deviation */
export function useSubscriptionTier(deviation?: PapiDeviation): UseSubscriptionTierReturnType {
  const currentUser = useCurrentUser();
  const isOwner = deviation ? isDeviationAuthor(deviation, currentUser) : false;

  const contextualTier = usePrimaryTier();
  const {
    isTier,
    isInTier,
    tier,
    isUserSubscribed,
    tierCheckoutUrl,
    subscriptionsTabUrl,
    needsSubscription,
    isSubscribedButBlocked,
  } = getBasicTierSubscriptionInfo(deviation, contextualTier);

  // you are never blocked if
  // - you're the content's owner,
  // - there's no subscription needed
  const subscriptionsMatter = !isOwner && !isTier;

  return {
    isTier,
    isInTier,
    tier,
    isOwner,
    isUserSubscribed,
    tierCheckoutUrl,
    subscriptionsTabUrl,
    needsSubscription: subscriptionsMatter && needsSubscription,
    isSubscribedButBlocked: subscriptionsMatter && isSubscribedButBlocked,
  };
}

/**
 * Get the basic tier info without react dependency
 */
export function getBasicTierSubscriptionInfo(
  deviation?: PapiDeviation,
  contextualTier?: PapiDeviation
) {
  const primaryTierFromDeviation = deviation && getPrimaryTier(deviation);
  const primaryTier = contextualTier || primaryTierFromDeviation;
  const isTier = deviation ? getDeviationType(deviation) === DeviationTypes.TIER : false;
  const isInTier = !!primaryTier;
  const tier = isTier ? deviation : primaryTier;
  const isUserSubscribed = isTier
    ? !!deviation?.tier?.isUserSubscribed
    : !!primaryTierFromDeviation?.tier?.isUserSubscribed;
  const tierCheckoutUrl = tier?.tier?.subproductId
    ? Url.checkoutLink(tier.tier.subproductId)
    : undefined;
  const subscriptionsTabUrl = tier?.author
    ? Url.userLink(tier.author.username, '/subscriptions')
    : undefined;
  const needsSubscription = deviation?.tierAccess === 'locked';
  const isSubscribedButBlocked = deviation?.tierAccess === 'locked-subscribed';

  return {
    isTier,
    isInTier,
    tier,
    isUserSubscribed: isUserSubscribed,
    tierCheckoutUrl,
    subscriptionsTabUrl,
    needsSubscription,
    isSubscribedButBlocked,
  };
}
